import { AnimationData, GameState, AnimationTargets } from '../types'

/**
 * Simple, universal animation logic:
 * - Animation flies TO the player who has the effect in their effectCards
 * - Animation starts FROM the card's last known position in prepared slots
 */
export const computeNewAnimations = (
  prevGameState: GameState | null,
  gameState: GameState | null,
  preparedCardRefs: { [key: string]: HTMLDivElement | null },
  effectCardRefs: { [key: string]: HTMLDivElement | null },
  targets: AnimationTargets,
  lastKnownRects: { [key: string]: DOMRect } = {}
): AnimationData[] => {
  if (!prevGameState || !gameState) return []
  if (!gameState.players || !prevGameState.players) return []
  const animations: AnimationData[] = []

  // For each player's effect cards in the NEW state
  gameState.players.forEach((player) => {
    player.effectCards.forEach(effectCard => {
      // Check if this card was in ANY player's prepared slots BEFORE
      let wasInPrepared = false

      prevGameState.players.forEach(prevPlayer => {
        if (prevPlayer.prepared.some(pc => pc.card.id === effectCard.id)) {
          wasInPrepared = true
        }
      })

      // Check if it was already an effect BEFORE (avoid re-animating)
      const wasInEffectCards = prevGameState.players.some(p =>
        p.effectCards.some(ec => ec.id === effectCard.id)
      )

      // Animate only if: was prepared, now is effect
      if (wasInPrepared && !wasInEffectCards) {
        // Get start position (try live ref, then cached)
        let startRect = preparedCardRefs[effectCard.id]?.getBoundingClientRect()
        if (!startRect) {
          startRect = lastKnownRects[effectCard.id]
        }

        if (startRect) {
          // Simple rule: fly to whoever has the effect
          animations.push({
            id: `${effectCard.id}-to-effect`,
            card: effectCard,
            startRect,
            type: 'to-effect',
            playerId: player.id // The player who now has this effect
          })
        }
      }
    })
  })

  // Handle cards LEAVING effects (visual cleanup)
  prevGameState.players.forEach(prevPlayer => {
    prevPlayer.effectCards.forEach(effectCard => {
      const isGone = !gameState.players.some(p =>
        p.effectCards.some(ec => ec.id === effectCard.id)
      )
      if (isGone) {
        const startRect = effectCardRefs[effectCard.id]?.getBoundingClientRect()
        if (startRect) {
          animations.push({
            id: `${effectCard.id}-from-effect`,
            card: effectCard,
            startRect,
            type: 'from-effect',
            playerId: prevPlayer.id
          })
        }
      }
    })
  })

  return animations
}
